import {
  Address,
  Customer,
  Document,
  DocumentSignatureStatus,
  DocumentType,
  Project,
  Shop,
  User,
  UserType,
} from '@prisma/client';

export function getProjectAddresses(project: {
  isDeliveryAddressOverride: boolean;
  isInstallationAddressOverride: boolean;
  isBillingAddressOverride: boolean;
  homeAddress: Address;
  deliveryAddress: Address;
  installationAddress: Address;
  billingAddress: Address;
}) {
  const result = {
    homeAddress: project.homeAddress,
    deliveryAddress: project.homeAddress,
    installationAddress: project.homeAddress,
    billingAddress: project.homeAddress,
  };

  if (project.isDeliveryAddressOverride) {
    result.deliveryAddress = project.deliveryAddress;
    result.installationAddress = project.deliveryAddress;
    result.billingAddress = project.deliveryAddress;
  }

  if (project.isInstallationAddressOverride) {
    result.installationAddress = project.installationAddress;
    result.billingAddress = project.installationAddress;
  }

  if (project.isBillingAddressOverride) {
    result.billingAddress = project.billingAddress;
  }

  return result;
}

const getStatusAdvancement = (status: ReturnType<typeof getQuoteStatus>) => {
  if (status === 'NOT_CREATED') return 0;
  if (status === 'CREATED') return 1;
  if (status === 'SENT_FOR_SIGNATURE_FIRST_TIME') return 2;
  if (status === 'SENT_FOR_SIGNATURE') return 3;
  if (status === 'SIGNED') return 4;
  return 0;
};

export function getProjectAdvancement(
  project: Project & { documents: Document[]; customer: Customer },
) {
  let advancement = 0;

  const quoteStatus = getQuoteStatus(project);
  const quoteAdvancement = getStatusAdvancement(quoteStatus);
  advancement += quoteAdvancement;
  if (quoteAdvancement !== 4) {
    return advancement;
  }

  const technicalPlanStatus = getTechnicalPlanStatus(project);
  const technicalPlanAdvancement = getStatusAdvancement(technicalPlanStatus);
  advancement += technicalPlanAdvancement;
  if (technicalPlanAdvancement !== 4) {
    return advancement;
  }

  if (!project.isOrderConfirmed) {
    return advancement;
  }
  advancement += 1;

  if (!project.isTechnicalPlanVerifiedOnSite) {
    return advancement;
  }
  advancement += 1;

  if (!project.hasCarrierReceived) {
    return advancement;
  }
  advancement += 1;

  if (!project.isConstructionSiteCompliant) {
    return advancement;
  }
  advancement += 1;

  return advancement + (project.customer.paidPercent.toNumber() ?? 0);
}

export function canUserSeeProject(
  project: Project,
  user: User & { shops: { id: string }[] },
) {
  switch (user.type) {
    case UserType.TECHNICIAN:
      return user.shops.some(shop => shop.id === project.shopId);
    case UserType.SELLER:
      return user.id === project.sellerId;
    case UserType.INSTALLER:
      return user.id === project.installerId;
    case UserType.ADMIN:
      return true;
    default:
      return false;
  }
}

export function canUserSeeShop(
  shop: Shop,
  user: User & { shops: { id: string }[] },
) {
  switch (user.type) {
    case UserType.ADMIN:
      return true;
    case UserType.TECHNICIAN:
    case UserType.SELLER:
      return user.shops.some(s => s.id === shop.id);
    case UserType.INSTALLER:
      return false;
    default:
      return false;
  }
}

export function canUserSeeCustomer(
  customer: Customer & { projects: { sellerId: string; shopId: string }[] },
  user: User & { shops: { id: string }[] },
) {
  switch (user.type) {
    case UserType.ADMIN:
      return true;
    case UserType.SELLER:
      return customer.projects.some(p => p.sellerId === user.id);
    case UserType.TECHNICIAN:
      return customer.projects.some(p =>
        user.shops.some(s => s.id === p.shopId),
      );
    case UserType.INSTALLER:
      return false;
    default:
      return false;
  }
}

export function getQuoteStatus(project: Project & { documents: Document[] }) {
  const quotes = project.documents.filter(
    document => document.type === DocumentType.QUOTE,
  );

  const isSignaturePending = quotes.some(
    document => document.signatureStatus === DocumentSignatureStatus.ONGOING,
  );

  if (isSignaturePending && project.isQuoteSigned) return 'SENT_FOR_SIGNATURE';

  if (isSignaturePending) return 'SENT_FOR_SIGNATURE_FIRST_TIME';

  if (project.isQuoteSigned) return 'SIGNED';

  if (project.isQuoteCreated) {
    return 'CREATED';
  }

  return 'NOT_CREATED';
}

export function getTechnicalPlanStatus(
  project: Project & { documents: Document[] },
) {
  const technicalPlans = project.documents.filter(
    document => document.type === DocumentType.TECHNICAL_PLAN,
  );

  const isSignaturePending = technicalPlans.some(
    document => document.signatureStatus === DocumentSignatureStatus.ONGOING,
  );

  if (isSignaturePending && project.isTechnicalPlanSigned)
    return 'SENT_FOR_SIGNATURE';

  if (isSignaturePending) return 'SENT_FOR_SIGNATURE_FIRST_TIME';

  if (project.isTechnicalPlanSigned) return 'SIGNED';

  if (project.isTechnicalPlanCreated) {
    return 'CREATED';
  }

  return 'NOT_CREATED';
}

type ProjectWithSupplies = {
  supplies: Array<{ itemName: string | null }>;
};

export function hasWorktop(project: ProjectWithSupplies) {
  return project.supplies.some(
    s => s.itemName != null && s.itemName.toLowerCase().includes('plan de travail'),
  );
}

export function hasSplashback(project: ProjectWithSupplies) {
  return project.supplies.some(
    s => s.itemName != null && /cr[eé]dence/i.test(s.itemName),
  );
}
